/**
 * Foydalanuvchi holati (demo `S` bilan bir xil shakl). Kurs bo'yicha alohida kalitda saqlanadi:
 * `lexo-v2-test` (EN) / `lexo-v2-test-ru` (RU). 2-haftada API'ga ko'chadi — shakl saqlanadi.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { BYID, COURSE_KEY, INTERVALS, LANG, LEVEL_ORDER, W, shuffle } from './data';
import type { Course, UiLang, Word } from './data/types';
import { COURSE_CODE, fire, sync } from './lib/sync';

export type Mode = 'free' | 'center';
export interface WordState { box: number; due: number; wr: number }
export interface UserSet { id: number; name: string; ids: number[]; created: number }
export interface DayLog { n: number; r: number }
export interface Survey { src?: string; age?: string; status?: string; why?: string; center?: string; learn?: Mode; at?: string }
/** Avatar konstruktori qiymatlari (indekslar — ui/Avatar AVOPT). */
export interface AvCfg { bg: number; skin: number; hair: number; hairC: number; eyes: number; mouth: number; acc: number }
export type Avatar = { t: 'build'; c: AvCfg } | { t: 'photo'; d: string };
export interface FormState { box: number; due: number }

export interface UserState {
  hm: number[];               // qiyin so'zlar (qo'lda)
  mode: Mode | null;
  survey: Survey;
  order: number[];            // mustaqil rejimda so'zlar tartibi
  sets: UserSet[];            // kartochkalarim
  activeSet: number | null;
  onb: boolean;
  name: string;
  ui: UiLang;
  goal: number;               // kunlik maqsad = bir darsdagi yangi so'zlar (3..10)
  sound: string;
  w: Record<number, WordState>;
  xp: number;
  streak: number;
  lastDay: number | null;
  newDay: number | null;
  newCount: number;
  offset: number;             // test: kunlarni oldinga surish
  log: Record<number, DayLog>;
  right: number;
  wrong: number;
  best: number | null;        // Juftlik, soniya
  best60?: number;
  bestMet?: number;
  sel: number[];              // o'yin uchun tanlangan so'zlar
  lvl: string;                // til darajasi (A1/A2/B1) — so'z jangida raqib shu bo'yicha
  nick: string | null;
  bio: string;
  avatar: Avatar | null;
  frame: string | null;       // avatar ramkasi (nomi)
  bestStreak: number;
  duels: { win: number; loss: number; draw: number };
  bestNum: Record<string, number>; // Sonlar: daraja → rekord
  fw: Record<string, FormState>;   // so'z shakllari (data/forms.ts id)
}

export const KEYOF = (l: Course) => (l === 'ru' ? 'lexo-v2-test-ru' : 'lexo-v2-test');
export const KEY = KEYOF(LANG);

export const fresh = (): UserState => ({
  hm: [], mode: null, survey: {}, order: [], sets: [], activeSet: null, onb: false, name: '', ui: 'uz', goal: 5, sound: 'marimba',
  w: {}, xp: 0, streak: 0, lastDay: null, newDay: null, newCount: 0, offset: 0, log: {}, right: 0, wrong: 0, best: null, sel: [],
  lvl: 'A1', nick: null, bio: '', avatar: null, frame: null, bestStreak: 0, duels: { win: 0, loss: 0, draw: 0 }, bestNum: {}, fw: {},
});

interface Store extends UserState {
  set: (patch: Partial<UserState> | ((s: UserState) => Partial<UserState>)) => void;
  /** Butun progressni tozalash (profil saqlanadi). */
  wipe: () => void;
  toggleHard: (id: number) => boolean;
}

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      ...fresh(),
      set: (patch) => set(typeof patch === 'function' ? patch(get()) : patch),
      wipe: () => {
        const s = get();
        const keep = { name: s.name, survey: s.survey, mode: s.mode, goal: s.goal, onb: s.onb, sound: s.sound, ui: s.ui, lvl: s.lvl, nick: s.nick, bio: s.bio, avatar: s.avatar, frame: s.frame };
        const order = shuffle(W.map((w) => w.id));
        set({ ...fresh(), ...keep, order });
        fire(sync.reset().then(() => sync.course({ order })), 'reset');
      },
      toggleHard: (id) => {
        const hm = get().hm ?? [];
        const on = !hm.includes(id);
        set({ hm: on ? [...hm, id] : hm.filter((x) => x !== id) });
        fire(sync.hard(id, on), 'hard');
        return on;
      },
    }),
    {
      name: KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => { const { set: _s, wipe: _w, toggleHard: _t, ...rest } = s; return rest; },
      merge: (persisted, current) => ({ ...current, ...fresh(), ...(persisted as Partial<UserState>) }),
    },
  ),
);

/* ---------- selektorlar (demo yordamchilari) ---------- */
export const S = () => useStore.getState();
export const today = () => Math.floor((Date.now() - new Date().getTimezoneOffset() * 60000) / 86400000) + (S().offset || 0);
export const st = (id: number): WordState | undefined => S().w[id];
export const newToday = () => (S().newDay === today() ? S().newCount : 0);
export const dueList = (): Word[] => W.filter((w) => st(w.id) && st(w.id)!.due <= today()).sort((a, b) => st(a.id)!.due - st(b.id)!.due);
export const unseen = (): Word[] => W.filter((w) => !st(w.id));
export const started = (): Word[] => W.filter((w) => !!st(w.id));
export const learnedN = () => W.filter((w) => st(w.id) && st(w.id)!.box >= 3).length;
export const isHard = (id: number) => (S().hm ?? []).includes(+id);
export const hardWords = (): Word[] => W.filter((w) => isHard(w.id));
export const streakNow = () => { const s = S(); return s.lastDay !== null && today() - s.lastDay <= 1 ? s.streak : 0; };
export const activeSet = (): UserSet | null => (S().sets ?? []).find((x) => x.id === S().activeSet) ?? null;
const dow = (d: number) => (((d + 3) % 7) + 7) % 7; // 0 = Dushanba
export const weekDays = (): number[] => { const tt = today(), s = tt - dow(tt); return [0, 1, 2, 3, 4, 5, 6].map((i) => s + i); };

/** Yangi so'zlar manbai: markaz rejimida joriy kartochka, aks holda tasodifiy tartib. */
export function lessonSource(): Word[] {
  const s = S();
  if (s.mode === 'center') {
    const a = activeSet();
    const fromSet = a ? a.ids.map((id) => BYID[id]).filter((w): w is Word => !!w && !st(w.id)) : [];
    if (fromSet.length) return fromSet;
  }
  // Tasodifiy tartib saqlanadi; bazaga keyin qo'shilgan so'zlar oxiriga (aralashtirilib) qo'shiladi
  let order = s.order ?? [];
  const inOrder = new Set(order);
  const added = W.filter((w) => !inOrder.has(w.id)).map((w) => w.id);
  if (added.length) { order = [...order.filter((id) => BYID[id]), ...shuffle(added)]; s.set({ order }); }
  // Avval tanlangan daraja, keyin undan yuqorilari, oxirida pastrog'i
  const lv = Math.max(0, LEVEL_ORDER.indexOf(s.lvl as (typeof LEVEL_ORDER)[number]));
  const rank = (w: Word) => { const i = LEVEL_ORDER.indexOf(w.level as (typeof LEVEL_ORDER)[number]); return i < 0 ? 99 : i >= lv ? i - lv : 10 + lv - i; };
  return order.map((id) => BYID[id]).filter((w): w is Word => !!w && !st(w.id))
    .map((w, i) => ({ w, i, r: rank(w) })).sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.w);
}

export function logDay(kind: 'n' | 'r', n = 1) {
  const tt = today();
  const log = { ...S().log };
  const d = { ...(log[tt] ?? { n: 0, r: 0 }) };
  d[kind] += n; log[tt] = d;
  S().set({ log });
}

/** Streak va XP (arcade va dars yakunida). */
export function bump(xp: number) {
  const tt = today(); const s = S();
  let streak = s.streak;
  if (s.lastDay === null || tt - s.lastDay > 1) streak = 1; else if (tt - s.lastDay === 1) streak++;
  s.set({ xp: s.xp + xp, streak, lastDay: tt });
  logDay('r', 0);
}

export { INTERVALS };

/** Kursni almashtirish: profil va so'rovnoma yangi kursga ko'chadi, progress alohida; sahifa qayta yuklanadi. */
export function switchCourse(to: Course) {
  if (to === LANG) return;
  try {
    const raw = localStorage.getItem(KEYOF(to));
    const o = raw ? (JSON.parse(raw) as { state?: Partial<UserState> }) : null;
    if (!o?.state?.onb) {
      const s = S();
      const copy: Partial<UserState> = { name: s.name, survey: s.survey, mode: s.mode, goal: s.goal, onb: s.onb, sound: s.sound, ui: s.ui };
      localStorage.setItem(KEYOF(to), JSON.stringify({ state: { ...(o?.state ?? {}), ...copy }, version: 0 }));
    }
    localStorage.setItem(COURSE_KEY, to);
  } catch { /* ignore */ }
  // Serverdagi joriy kurs ham almashadi; javob kutilmaydi (bootstrap baribir LANG bo'yicha so'raydi)
  sync.me({ course: COURSE_CODE[to] }).catch(() => {}).finally(() => location.reload());
}
